import React, { useContext, useEffect, useRef } from 'react';
import { ChatbotContext } from '../contexts/ChatbotContext';
import { CAContext } from '../contexts/CAContext';
import { useCBChatStart } from './chatbot/useCBChatStart';
import { useCBChatMessage } from './chatbot/useCBChatMessage';
import { useCBChatEnd } from './chatbot/useCBChatEnd';
import { useCBChatRetrieve } from './chatbot/useCBChatRetrieve';
import TypingIndicator from '../components/TypingIndicator/TypingIndicator';
import { util } from '../utils/miaw/common';
import { setChatSessionType } from '../utils/session-storage-utils';
import { useCustomTracking } from './custom-hooks/useTracking';
import { useNRLogging } from './custom-hooks/useNRLogging';
import { useCustomChatEvents } from './custom-hooks/useCustomChatEvents';

export const useChatbotHooks = () => {
  const { sendTrackingEvent } = useCustomTracking();
  const prevUnreadMessagesRef = useRef<number>(0);
  const { logCustomAttributesNR } = useNRLogging();
  const { sendChatEvents } = useCustomChatEvents();
  const {
    chatState: { chatWindow, chatSession, unreadMessages, playSoundMessageNotif },
    setChatState,
  } = useContext(CAContext);
  const {
    state: { customFlow, sessionId, chatbotFlowRunId, flowAttemptCounts },
    setState: setChatbotContext,
  } = useContext(ChatbotContext);

  // Initialize all Chatbot hooks
  const { data: chatbotStartData, trigger: triggerStartChatBot, loading: chatbotStartLoading, error: chatbotStartError } = useCBChatStart();

  const {
    data: chatbotMessageData,
    trigger: triggerChatbotMessage,
    cancel: cancelChatbotMessage,
    loading: chatbotMessageLoading,
    error: chatbotMessageError,
  } = useCBChatMessage();

  const { data: chatbotEndData, trigger: triggerChatbotEnd, loading: chatbotEndLoading, error: chatbotEndError } = useCBChatEnd();

  const { data: chatbotRetrieveData, trigger: triggerChatbotRetrieve, loading: chatbotRetrieveLoading, error: chatbotRetrieveError } = useCBChatRetrieve();

  // Handle chatbot start response
  useEffect(() => {
    if (chatbotStartData) {
      console.log('Chatbot Start Data:', chatbotStartData);
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages,
          // chatbotStartData.message, TODO : message will be hardcoded for now until API is fast enough to return a message
        ],
        sessionId: chatbotStartData.sessionId,
        discussionId: chatbotStartData.discussionId,
      }));
      setChatSessionType('chatbotSession');
    }
  }, [chatbotStartData]);

  // Handle chatbot message response
  useEffect(() => {
    if (chatbotMessageData) {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: [...prevState.conversationMessages, ...chatbotMessageData.messages],
      }));
    }
  }, [chatbotMessageData]);

  useEffect(() => {
    if (chatbotMessageData) {
      console.log('Chatbot Message Data:', chatbotMessageData);
      const messageCount = chatbotMessageData.messages.length;
      setChatState((prevState) => ({
        ...prevState,
        unreadMessages: prevState.unreadMessages ? prevState.unreadMessages + messageCount : messageCount,
      }));
    }
  }, [chatbotMessageData]);

  // Play audio when unreadMessages increments and customer is away
  useEffect(() => {
    const currentUnreadCount = unreadMessages || 0;
    const shouldPlayAudio = currentUnreadCount > prevUnreadMessagesRef.current && currentUnreadCount > 0 && (document.hidden || chatWindow === 'minimized');

    if (shouldPlayAudio) {
      playSoundMessageNotif?.();
    }

    prevUnreadMessagesRef.current = currentUnreadCount;
  }, [unreadMessages, chatWindow, playSoundMessageNotif]);

  useEffect(() => {
    if (chatbotMessageData) {
      console.log('Chatbot Message Data:', chatbotMessageData);
      const flowType = chatbotMessageData.flowType;
      if (flowType) {
        // If flowType is not set, start flow will trigger
        // Else if a new flowType is detected, start the new flow
        if (flowType.agentConnect) {
          // If agent is connected, do not change the flow
          return;
        }
        if (!customFlow || customFlow.flowType !== flowType?.flowType) {
          const isTransfer = customFlow && customFlow.flowType && customFlow.flowType !== flowType?.flowType;

          // existing flow to a different flow
          if (isTransfer) {
            sendTrackingEvent('Chatbot Clicked', 'Chatbot:OrderFlow', 'chatbotFlowTransferred', 'GEN AI Chatbot', {
              chatBotSessionId: sessionId || '',
              chatbotFlowRunId: chatbotFlowRunId || '',
              chatbotFlowId_from: customFlow.flowType || '',
              chatbotFlowStep_last: customFlow.flowStep || '',
              chatbotFlowId_to: flowType?.flowType || '',
              timestamp: new Date().getTime().toString(),
            });
            logCustomAttributesNR('ca-chatbot-info', {
              logName: 'chatbot_flow_transferred',
              logLevel: 'info',
              message: `User transferred from flow ${customFlow.flowType || ''} to ${flowType?.flowType || ''}`,
              chatbotFlowRunId: chatbotFlowRunId || '',
              flowType_from: customFlow.flowType || '',
              flowStep_last: customFlow.flowStep || '',
              flowType_to: flowType?.flowType || '',
            });
          }

          // Generate a new unique run ID for the new flow based on attempt count
          const currentFlowType = flowType?.flowType || 'flow';
          const currentAttemptCount = (flowAttemptCounts?.[currentFlowType] || 0) + 1;
          const newFlowRunId = `RUN_${currentFlowType}_${currentAttemptCount.toString().padStart(3, '0')}`;

          setChatbotContext((prevState) => ({
            ...prevState,
            customFlow: flowType,
            chatbotFlowRunId: newFlowRunId,
            flowAttemptCounts: {
              ...prevState.flowAttemptCounts,
              [currentFlowType]: currentAttemptCount,
            },
          }));
          // Trigger start flow
          sendTrackingEvent('Chatbot Clicked', 'Chatbot:OrderFlow', 'chatbotFlowStarted', 'GEN AI Chatbot', {
            chatbotFlowId: flowType?.flowType || '',
            chatbotFlowRunId: newFlowRunId || '',
            chatbotFlowStep: flowType?.flowStep || '',
            chatbotFlowAdditionalDetails: flowType?.additionalInfo || '',
          });
          logCustomAttributesNR('ca-chatbot-info', {
            logName: 'chatbot_flow_started',
            logLevel: 'info',
            message: `User started a new chatbot flow : ${flowType?.flowType || ''}`,
            flowType: flowType?.flowType || '',
            flowStep: flowType?.flowStep || '',
          });
        } else if (chatbotMessageData.flowType?.flowStep === 'escalation') {
          sendTrackingEvent('Chatbot Clicked', 'Chatbot:OrderFlow', 'chatbotFlowEscalated', 'GEN AI Chatbot', {
            chatbotFlowId: flowType?.flowType || '',
            chatbotFlowStep: flowType?.flowStep || '',
            chatbotFlowAdditionalDetails: flowType?.additionalInfo || '',
          });
          setChatbotContext((prevState) => ({
            ...prevState,
            customFlow: undefined,
          }));
          logCustomAttributesNR('ca-chatbot-info', {
            logName: 'chatbot_flow_escalated',
            logLevel: 'info',
            message: `User escalated a chatbot flow : ${flowType?.flowType || ''}`,
            flowType: flowType?.flowType || '',
            flowStep: flowType?.flowStep || '',
          });
        } else if (
          chatbotMessageData.flowType?.flowStep === 'tracking_response' || // For order tracking flow
          chatbotMessageData.flowType?.flowStep === 'products_cancelled' // For order cancellation flow
        ) {
          setChatbotContext((prevState) => ({
            ...prevState,
            customFlow: undefined,
          }));
          sendTrackingEvent('Chatbot Clicked', 'Chatbot:OrderFlow', 'chatbotFlowCompleted', 'GEN AI Chatbot', {
            chatbotFlowId: flowType?.flowType || '',
            chatbotFlowRunId: chatbotFlowRunId || '',
            chatbotFlowStep: flowType?.flowStep || '',
            chatbotFlowAdditionalDetails: flowType?.additionalInfo || '',
          });
          logCustomAttributesNR('ca-chatbot-info', {
            logName: 'chatbot_flow_completed',
            logLevel: 'info',
            message: `User completed a chatbot flow : ${flowType?.flowType || ''}`,
            flowType: flowType?.flowType || '',
            flowStep: flowType?.flowStep || '',
          });
        } else {
          sendTrackingEvent('Chatbot Clicked', 'Chatbot:OrderFlow', 'chatbotFlowStep', 'GEN AI Chatbot', {
            chatbotFlowId: flowType?.flowType || '',
            chatbotFlowStep: flowType?.flowStep || '',
            chatbotFlowAdditionalDetails: flowType?.additionalInfo || '',
          });
          logCustomAttributesNR('ca-chatbot-info', {
            logName: 'chatbot_flow_step',
            logLevel: 'info',
            message: `User progressed to chatbot flow step : ${flowType?.flowStep || ''}`,
            flowType: flowType?.flowType || '',
            flowStep: flowType?.flowStep || '',
          });
        }
      } else {
        // Reset Flow if no response received
        setChatbotContext((prevState) => ({
          ...prevState,
          customFlow: undefined,
        }));
      }
    }
  }, [chatbotMessageData]);

  // Handle chatbot loading state (typing indicator)
  useEffect(() => {
    if (chatbotMessageLoading) {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            type: 'Typing',
            id: util.generateUUID(),
            messageContent: <TypingIndicator senderType="chatbot" key={util.generateUUID()} />,
            timestamp: new Date().getTime(),
            senderDisplayName: 'Chatbot',
            showFeedback: false,
          },
        ],
      }));
    } else {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.filter((item) => item.type !== 'Typing'),
      }));
    }
  }, [chatbotMessageLoading]);

  // Mark all messages as read when chat window is minimized
  useEffect(() => {
    if (chatWindow === 'minimized') {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => ({ ...message, isMessageRead: true })),
      }));
    }
  }, [chatWindow]);

  // Handle chatbot end response
  useEffect(() => {
    if (chatbotEndData) {
      setChatState((prevState) => ({
        ...prevState,
        chatSession: 'endChatSession',
      }));
    }
  }, [chatbotEndData]);

  useEffect(() => {
    if (chatSession === 'initial')
      setChatbotContext((prevState) => ({
        ...prevState,
        endChatData: undefined,
        endChatLoading: false,
      }));
  }, [chatSession]);

  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      endChatLoading: chatbotEndLoading,
    }));
  }, [chatbotEndLoading]);

  // Handle chatbot retrieve response
  useEffect(() => {
    if (chatbotRetrieveData && (chatSession === 'chatbotSession' || chatSession === 'miawChatSession')) {
      console.log('Chatbot Retrieve Data:', chatbotRetrieveData);
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: [...chatbotRetrieveData.messages, ...prevState.conversationMessages],
        sessionId: chatbotRetrieveData.sessionId,
        discussionId: chatbotRetrieveData.discussionId,
      }));
      sendChatEvents('onSessionContinuation', {});
    }
  }, [chatbotRetrieveData]);

  // Set up Chatbot hooks in context
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      startChatbotSession: triggerStartChatBot,
    }));
  }, [triggerStartChatBot]);

  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      sendChatbotMessage: triggerChatbotMessage,
    }));
  }, [triggerChatbotMessage]);

  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      endChatbotSession: triggerChatbotEnd,
    }));
  }, [triggerChatbotEnd]);

  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      isSendMessageLoading: chatbotMessageLoading,
    }));
  }, [chatbotMessageLoading]);

  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      isSendMessageLoading: chatbotStartLoading,
    }));
  }, [chatbotStartLoading]);

  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      isEndChatLoading: chatbotRetrieveLoading,
    }));
  }, [chatbotRetrieveLoading]);

  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      stopChatbotMessage: cancelChatbotMessage,
    }));
  }, [cancelChatbotMessage]);

  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      retrieveChatbotSession: triggerChatbotRetrieve,
    }));
  }, [triggerChatbotRetrieve]);

  // Handle errors and update error state
  useEffect(() => {
    const hasErrors = chatbotStartError || chatbotMessageError;

    if (hasErrors) {
      setChatbotContext((prevState) => ({
        ...prevState,
        showErrorMessage: true,
      }));
      console.error('Chatbot Error:', {
        startError: chatbotStartError,
        messageError: chatbotMessageError,
      });
    } else {
      setChatbotContext((prevState) => ({
        ...prevState,
        showErrorMessage: false,
      }));
    }
  }, [chatbotStartError, chatbotMessageError]);

  /** Show error message for chatbot message retrieval failure */

  useEffect(() => {
    if (chatSession !== 'chatbotSession') {
      setChatbotContext((prevState) => ({
        ...prevState,
        showErrorMessage: false,
      }));
    }
    if (chatSession === 'chatbotSession' && chatbotRetrieveError) {
      setChatbotContext((prevState) => ({
        ...prevState,
        showErrorMessage: true,
      }));
    }
  }, [chatSession, chatbotRetrieveError]);

  /** Reset error state once chat is ended */
  useEffect(() => {
    if (chatSession === 'initial') {
      setChatbotContext((prevState) => ({
        ...prevState,
        showErrorMessage: false,
      }));
    }
  }, [chatSession]);

  useEffect(() => {
    if (chatbotEndError) {
      setChatbotContext((prevState) => ({
        ...prevState,
        isChatbotEndError: true,
      }));
    }
  }, [chatbotEndError]);

  useEffect(() => {
    if (chatSession === 'initial') {
      setChatbotContext((prevState) => ({
        ...prevState,
        isChatbotEndError: false,
      }));
    }
  }, [chatSession]);

  return {
    chatbotHooksData: {
      chatbotStartData,
      chatbotMessageData,
      chatbotEndData,
      chatbotRetrieveData,
    },
    chatbotHooksLoading: {
      chatbotStartLoading,
      chatbotMessageLoading,
      chatbotEndLoading,
      chatbotRetrieveLoading,
    },
    chatbotHooksError: {
      chatbotStartError,
      chatbotMessageError,
      chatbotEndError,
      chatbotRetrieveError,
    },
    chatbotHooksTriggers: {
      triggerStartChatBot,
      triggerChatbotMessage,
      triggerChatbotEnd,
      cancelChatbotMessage,
      triggerChatbotRetrieve,
    },
  };
};
